import { useState, useEffect, useRef, useCallback } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import { onGoalBus } from '@/lib/goalBus'

interface Goal {
  id: number
  name: string
  type: string
  goalValue: number
  period: string
  exerciseTypes: string | null
  currentProgress: number
  status: string
  currentStreak: number
}

interface GoalEntry {
  goal: Goal
  streakRewards: unknown[]
}

const SYNC_INTERVAL_MS = 60_000

export default function GoalProgressBar() {
  const [goals, setGoals] = useState<Goal[]>([])
  const [loaded, setLoaded] = useState(false)
  // Seconds ticked locally since the last server sync, while a session is
  // active and the kid isn't idle. Keeps the bar visibly filling between
  // heartbeats; each server sync re-bases it.
  const [localSeconds, setLocalSeconds] = useState(0)
  const sessionRef = useRef({ active: false, idle: false })
  // Last displayed seconds per goal, so a server sync (which lags the local
  // tick by up to a heartbeat) never makes the bar move backward.
  const displayFloorRef = useRef(new Map<number, number>())

  const applyGoals = useCallback((next: Goal[]) => {
    for (const g of next) {
      if (g.type !== 'DURATION') continue
      const serverSec = g.currentProgress * 60
      const floor = displayFloorRef.current.get(g.id) ?? 0
      // A large drop is a real reset (new day/week) — accept it.
      if (serverSec + 300 < floor) displayFloorRef.current.delete(g.id)
    }
    setGoals(next)
    setLocalSeconds(0)
    setLoaded(true)
  }, [])

  const sync = useCallback(async () => {
    try {
      const data = await apiFetch<GoalEntry[]>('/goals/mine')
      applyGoals(data.map(e => e.goal))
    } catch {
      setLoaded(true)
    }
  }, [applyGoals])

  useEffect(() => {
    sync()
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') sync()
    }, SYNC_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [sync])

  // Fresh goals pushed from endSession's /goals/report-progress response
  useEffect(() => onGoalBus('goals-updated', payload => {
    applyGoals(payload as Goal[])
  }), [applyGoals])

  useEffect(() => onGoalBus('session-state', s => {
    sessionRef.current = s
  }), [])

  useEffect(() => {
    const tick = setInterval(() => {
      if (sessionRef.current.active && !sessionRef.current.idle) {
        setLocalSeconds(s => s + 1)
      }
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  if (!loaded || goals.length === 0) return null

  return (
    <div className="space-y-2">
      {goals.map(g => {
        const isDuration = g.type === 'DURATION'
        let progressSec = g.currentProgress * 60
        if (isDuration) {
          const floor = displayFloorRef.current.get(g.id) ?? 0
          progressSec = Math.max(progressSec + localSeconds, floor)
          displayFloorRef.current.set(g.id, progressSec)
        }
        const goalSec = g.goalValue * 60
        const shown = isDuration ? Math.floor(progressSec / 60) : g.currentProgress
        const pct = isDuration
          ? Math.min(100, (progressSec / goalSec) * 100)
          : Math.min(100, Math.round((g.currentProgress / g.goalValue) * 100))
        const unit = isDuration ? 'min' : g.type === 'ACT_QUESTIONS' ? 'questions' : 'words'
        const periodStr = g.type === 'LIST_MASTERY' ? '' : (g.period === 'WEEKLY' ? 'this week' : 'today')
        return (
          <div key={g.id} className="flex items-center gap-3 text-sm">
            <div className="flex-1 bg-accent rounded-full h-2.5 overflow-hidden">
              <div className="bg-primary h-full rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {shown}/{g.goalValue} {unit} {periodStr}
              {g.currentStreak > 0 && (
                <span className="ml-1 text-amber-500">🔥{g.currentStreak}</span>
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}
